import React, { useEffect, useRef, useState } from 'react';
import Color from '../../../data/Color';
import Flex from '../../../data/Flex';
import Size from '../../../data/Size';
import { dispatch, listen, unlisten } from '../../../dispatcher/Dispatcher';
import AppEvent from '../../../events/AppEvent';
import MapEvent from '../../../events/MapEvent';
import UIButton from '../../../ui/UIButton';
import { UIWhiteBox } from '../../../ui/UIContainer';
import UIFlex from '../../../ui/UIFlex';
import { UIForm } from '../../../ui/UIForm';
import UIIcon from '../../../ui/UIIcon';
import UIInput from '../../../ui/UIInput';
import UITitle from '../../../ui/UITitle';
import UIToggleButton from '../../../ui/UIToggleButton';
import FormatUtil from '../../../util/FormatUtil';
import { IGeocodeResult } from './MainViewMap';
import Api from '../../../api/Api';
import { ICDRData, handleSetICDRData } from '../../../data/CDRData';
import ConfirmOverlay from "../../../ui/overlay/ConfirmOverlay";
import './Provisioning.scss';
import { UILoadingBox } from '../../../ui/UILoadingBox';
import useLoadingState from '../../../hooks/useLoadingState';
import LoadingOverlay from '../../../ui/overlay/LoadingOverlay';
import User from '../../../data/User';
import { TransferNumber } from './TransferNumber';

export interface IProvisioningProps {
    introDelay:number;
}

export interface IProvisioningState{
    cbn:string;
    callerName:string;
    address:string;
    location_exact_comment:string;
    city:string;
    state:string;
    zipCode:string;
    country:string;
}

export function Provisioning($props:IProvisioningProps){
    

    const [syncChangesToMap, setSyncToMap] = useState(!User.state.suppressMapUpdates);
    

    const [loadingState, setLoading, setError, setLoadingState, resetLoadingState] = useLoadingState({loading:false});

    const [validFieldsUpdate, setShowValidFieldsUpdate] = useState(false);

    const defaultState:IProvisioningState = {
        cbn:"",
        callerName:"",
        address:"",
        location_exact_comment:"",
        city:"",
        state:"",
        zipCode:"",
        country:"US",
    }

    const [state, setState] = useState<IProvisioningState>(defaultState);
    
    const validFieldsUpdateTimeoutID = useRef(-1);

    const animateValidFieldsUpdate=()=>{
        setShowValidFieldsUpdate(true);

        window.clearTimeout(validFieldsUpdateTimeoutID.current);
        validFieldsUpdateTimeoutID.current = window.setTimeout(()=>{
            setShowValidFieldsUpdate(false);
        },100);
    }



    const manualInputChange=($value:string | number | boolean, $name:string)=>{        
        setState(($prevState)=>{
            let newState:any = {...$prevState};
            newState[$name] = $value;
            updateMapToAddress(newState);
            return newState;
        });
    }
    const updateMapToAddress=($state:IProvisioningState)=>{
        if(syncChangesToMap){
            dispatch(MapEvent.UPDATE_MAP_TO_ADDRESS,{state:$state});
        }
    }
    
    const _onChosenLocation=($event:MapEvent)=>{

        let data:IGeocodeResult = $event.data;    

        let newState:IProvisioningState = {...state}

        newState.address = data.street_number+" "+data.route;
        newState.location_exact_comment = "";//blank for geo results
        newState.city = data.city;
        newState.state = data.state;
        newState.zipCode = data.postalCode;
        newState.callerName = data.title || "";
        newState.country = data.country;
        newState.cbn = data.cbn || "";

        animateValidFieldsUpdate();
        
        setState(newState);
    }

    const _onCDRAction=($event:AppEvent)=>{
      let cdrData:ICDRData = $event.data;     
      setCDRData(cdrData);
    }
    const setCDRData=($cdr:ICDRData)=>{
        let newState:IProvisioningState = {...state}

        newState.address = $cdr.address;
        newState.cbn = $cdr.cbn;
        newState.city = $cdr.city + "";
        newState.country = $cdr.country + "";
        newState.state = $cdr.state + "";
        newState.zipCode = $cdr.zipcode +"";
        newState.callerName = $cdr.name + "";
        newState.location_exact_comment = $cdr.location + "";

                
        updateMapToAddress(newState);
        animateValidFieldsUpdate();
        setState(newState);

    }

    const manualCBNInputChange=($value:string | number | boolean, $name:string)=>{        

        let newState = {...state}
        newState.cbn = $value as string;
        setState(newState);

        dispatch(AppEvent.GOT_CALLBACK_NUMBER,{cbn:{...state}.cbn});
    }

    const _onCBNLookupAction = ()=>{

        setLoadingState({loading:true, error:undefined});
        
        let cbn = state.cbn?state.cbn.trim():"";  

        if(cbn===""){
            return;
        }   

        Api.cdr.getCDRS(($success,$results)=>{
            if($success){
                setLoadingState({loading:false});

                let cdrDataArray = handleSetICDRData($results.results);
                if(cdrDataArray.length>0){
                    let cdrData:ICDRData = cdrDataArray[0];
                    setCDRData(cdrData);
                }
            }else{

                setLoadingState({loading:false, error:$results});
                ConfirmOverlay.show("errorAddressNotFound",{confirmLabel:"Ok", hideCancel:true, title:"Error", message:"Callback Number Not Found"});
            }
        },{callbackNumber:cbn,duration:86400});
    }

    const generate911TransferNum = ()=>{

        let capturedState:IProvisioningState = {...state}

        const hideLoading = LoadingOverlay.show("generating911Transfer",{title:"Loading Please Wait", desc:"Generating 911 Transfer Number..."});
        Api.cdr.generate911Transfer(($success,$results)=>{
            hideLoading();
            if($success){

                let transferNumber:string = ""
                if($results.transferNumber){
                    transferNumber = $results.transferNumber;
                }

                let expirationSeconds:Number = 0;
                if($results.gatewayExpireSeconds){
                    expirationSeconds = Number($results.gatewayExpireSeconds);
                }

                dispatch(AppEvent.GENERATED_911_TRANSFER_NUM, {expirationSeconds, transferNumber});
            }else{
                ConfirmOverlay.show("errorGen911Transfer",{confirmLabel:"Ok", hideCancel:true, title:"Error", message:"Unable to Generate 911 Transfer Number."});
            }
        },capturedState);
    }

    useEffect(()=>{
        listen(AppEvent.VIEW_CDR_DETAILS,_onCDRAction);
        listen(MapEvent.USE_CHOSEN_LOCATION,_onChosenLocation);
      
        return ()=>{
            unlisten(MapEvent.USE_CHOSEN_LOCATION,_onChosenLocation);
            unlisten(AppEvent.VIEW_CDR_DETAILS,_onCDRAction);
            window.clearTimeout(validFieldsUpdateTimeoutID.current);
        }
    },[]);


    let stateOptions = FormatUtil.STATES_US_AS_UI_OPTIONS;
    let stateLabel:string = "State";
    let zipLabel:string = "Zip Code";
    if(state.country==="CA"){
        stateLabel = "Province";
        zipLabel = "Postal Code";
        stateOptions = FormatUtil.STATES_CA_AS_UI_OPTIONS;
    }

    let canGenerateDisabled:boolean=true;

    if(state.city.trim() && state.state.trim() && state.address.trim() && state.cbn && state.cbn.trim()){
        canGenerateDisabled=false;
    }

    let generateFieldsCN:string = "generateFields";

    let disableCbnLookup = state.cbn?false:true;

    if(validFieldsUpdate){
        generateFieldsCN+=" flash";
    }

    return (
        <UIWhiteBox className='provisioning' flex={{fullWidth:true, style:{gap:20}}} animateIntro animateIntroDelay={$props.introDelay}>
            <UITitle flex={{style:{marginBottom:10}}} title="Provisioning"/>
            {/*
                <UIFlex flex={{direction:Flex.COLUMN, alignItems:Flex.FLEX_END, style:{fontWeight:600, position:"absolute", top:10, right:10, gap:5, fontSize:10}}}>
                    UPDATE MAP ON CHANGES
                    <UIToggleButton size={Size.s30} onChange={($value)=>{
                        setSyncToMap($value);

                        if($value){
                            dispatch(MapEvent.UPDATE_TO_CHANGE,{state:state});
                        }
                    }} flex={{style:{width:60}}} fontSize={Size.s10} dragWidth={30} enabled={syncChangesToMap} name='syncToMap'/>
                </UIFlex>
            */}
            
            <UIForm flex={{style:{gap:20}}} className={generateFieldsCN}>
                <UIInput 
                    className='callbackNumber' 
                    flex={{style:User.state.isECRCUser?{borderRight:"0px", borderTopRightRadius:0, borderBottomRightRadius:0}:{}}} 
                    fontSize={Size.s14} 
                    size={Size.s36} 
                    label="Callback Number" 
                    name='cbn' 
                    value={state.cbn}
                    isRequired
                    onChange={manualCBNInputChange}
                    onBlur={manualCBNInputChange}
                    valueExtra={
                        (
                            <>
                                {User.state.isECRCUser && (
                                    <UIButton dropShadow size={Size.s36} disabled={disableCbnLookup} fontSize={Size.s20} isSquare icon={UIIcon.SEARCH_LOCATION} onClick={()=>{
                                        _onCBNLookupAction();
                                    }}/>
                                )}
                            
                            </>
                        )
                    } 
                > 
                    {loadingState.loading && (
                        <UILoadingBox flex={{style:{marginTop:15}}}/>
                    )}
                </UIInput>
                <UIInput flex={{fullWidth:true}} fontSize={Size.s14} size={Size.s36} label="Caller/Business Name" name='callerName' value={state.callerName} onChange={manualInputChange} />    
                <UIInput flex={{fullWidth:true}} fontSize={Size.s14} size={Size.s36} label="Street Number and Name" name='address' value={state.address} onChange={manualInputChange} isRequired  />    
                <UIInput flex={{fullWidth:true}} fontSize={Size.s14} size={Size.s36} label="Loc/Exact/Comment" name='location_exact_comment' value={state.location_exact_comment} onChange={manualInputChange}  />     
                <UIInput flex={{fullWidth:true}} fontSize={Size.s14} size={Size.s36} label="City" isRequired name='city' value={state.city} onChange={manualInputChange}  /> 
                <UIFlex flex={{fullWidth:true, style:{gap:10}}}>    
                    <UIInput flex={{style:{width:100, flexGrow:1, flexShrink:0}}} fontSize={Size.s14} size={Size.s36} label={stateLabel} type={UIInput.TYPE_SELECT} onChange={manualInputChange}  options={stateOptions} value={state.state} isRequired name='state' /> 
                    <UIInput flex={{style:{width:80}}} fontSize={Size.s14} size={Size.s36} label={zipLabel} name='zipCode' value={state.zipCode} onChange={manualInputChange}  /> 
                    <UIInput flex={{style:{width:60}}} fontSize={Size.s14} size={Size.s36} label="Country" name='country' type={UIInput.TYPE_SELECT} onChange={manualInputChange} options={FormatUtil.COUNTRIES_AS_UI_OPTIONS} value={state.country} />                     
                </UIFlex>  
            </UIForm>
            <UIFlex flex={{style:{gap:10}}}>
                <UIButton flex={{style:{whiteSpace:"nowrap"}, fullWidth:true}} disabled={canGenerateDisabled} onClick={()=>{
                    generate911TransferNum();
                }} dropShadow color={Color.PRIMARY} label="Generate 911 Transfer Number"/> 
                <UIButton label={"Clear"} onClick={()=>{
                    let newState:IProvisioningState = {
                        address:"",
                        callerName:"",
                        cbn:"",
                        city:"",
                        country:"",
                        location_exact_comment:"",
                        state:"US",
                        zipCode:""
                    }
                    setState(newState);
                }}/>

            </UIFlex>
           <TransferNumber/>
        </UIWhiteBox>
    );
}
